/**
 * Dealer-scope query params helper.
 *
 * На standalone-демо (demo.shiftgears.ai / *.meteora.pro) активный дилер
 * резолвится ТОЛЬКО из URL-параметров при загрузке: showroomTokenResolver
 * читает `?token` (или `organizationId`/`inventorySlug`) и кладёт resolved
 * org/slug в `window.b2bMountParams`, откуда их подхватывают config-getter'ы
 * в runtimeConfig.js. Ничего не персистится — это чисто in-memory за одну
 * загрузку.
 *
 * Проблема: React Router `navigate('/cars/:id/landing')` переписывает
 * весь hash и роняет query-строку. Поэтому после ЛЮБОЙ внутренней навигации
 * (salon → машина, каталог → машина, свайп между лендингами) URL остаётся без
 * `?token`, и при reload резолвер не находит токен → fallback на дефолтный
 * org=1 / demo-autopolis → статический Autodis-каталог (org 4) не грузится →
 * `getCarById()` возвращает undefined → «Car not found».
 *
 * Фикс: переносить dealer-scope параметры вперёд при каждой навигации внутри
 * showroom-flow, чтобы каждый достижимый URL сам описывал дилера и reload
 * всегда резолвил правильный inventory.
 */

// Только параметры, определяющие дилера/inventory. Тему на standalone
// применяет useDealerPreset на самой странице (не из токена), поэтому
// theme-параметры переносить не нужно.
const DEALER_SCOPE_KEYS = ['token', 'organizationId', 'inventorySlug'];

/**
 * Собрать `search`-строку с dealer-scope параметрами из текущего location.
 *
 * @param {string} [currentSearch] - текущий `location.search` (из useLocation()).
 *   Под HashRouter это query-часть внутри hash (`#/path?token=...` → `?token=...`).
 * @returns {string} search-строка, начинающаяся с '?' (или '' если переносить нечего).
 */
export const buildDealerScopeSearch = (currentSearch) => {
  const src = new URLSearchParams(currentSearch || '');
  const out = new URLSearchParams();
  for (const key of DEALER_SCOPE_KEYS) {
    const value = src.get(key);
    if (value) out.set(key, value);
  }
  const serialized = out.toString();
  return serialized ? `?${serialized}` : '';
};
